import { useState, useEffect } from "react";
import { GoogleLogo, MicrosoftLogo, YahooLogo, MailLogo, accountLogo } from "./BrandLogos";
import SmtpConnectForm from "./SmtpConnectForm";
import { C } from "./ui/theme";
// Phase 3 (standalone page-file token migration, see linear-brewing-toast.md)
// — replaced the local `T` Fluent palette with the shared `C` Apple tokens
// (pure name-swap, same values in the same shape as everywhere else).

export default function ConnectedAccounts({ addToast }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSmtp, setShowSmtp] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const token = () => localStorage.getItem("sdr_token") || "";
  const authH = () => ({ "Authorization": "Bearer " + token(), "Content-Type": "application/json" });
  const loadAccounts = () => {
    setLoading(true);
    fetch("/api/smtp/accounts", { headers: authH() })
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setAccounts(d); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { loadAccounts(); }, []);
  const syncNow = async (email) => {
    setSyncingId(email);
    try {
      const r = await fetch("/api/smtp/sync-now", { method: "POST", headers: authH(), body: JSON.stringify({ email }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Sync failed");
      const result = d.results?.[0];
      if (result?.error) addToast("Sync error: " + result.error, "error");
      else addToast("Synced " + email, "success");
      loadAccounts();
    } catch(e) { addToast(e.message, "error"); }
    setSyncingId(null);
  };
  const disconnect = async (id) => {
    try {
      await fetch("/api/smtp/accounts/" + id, { method: "DELETE", headers: authH() });
      setAccounts(p => p.filter(a => a.id !== id));
      addToast("Account disconnected", "info");
    } catch(e) { addToast(e.message, "error"); }
  };
  const setDefault = async (id) => {
    try {
      const r = await fetch("/api/smtp/accounts/" + id, { method: "PATCH", headers: authH(), body: JSON.stringify({ isDefault: true }) });
      if (!r.ok) throw new Error((await r.json()).error || "Failed to set as primary");
      addToast("Primary sending account updated", "success");
      loadAccounts();
    } catch(e) { addToast(e.message, "error"); }
  };
  return (
    <div style={{ background: "#fff", border: "1px solid " + C.border, borderRadius: 12, padding: "18px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <p style={{ fontSize: 13, fontWeight: 600 }}>Connected Email Accounts</p>
          <p style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>Connect your email to send from your own mailbox — works with Gmail, Outlook, M365, and any SMTP server</p>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={() => { window.location.href = "/api/oauth/google/start?token=" + encodeURIComponent(token()); }}
            style={{ display:"flex", alignItems:"center", gap:8, background: "#fff", color: "#3c4043", border: "1px solid " + C.border, borderRadius: 6, padding: "8px 16px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
            <GoogleLogo size={16}/> Connect with Google
          </button>
          <button onClick={() => { window.location.href = "/api/oauth/microsoft/start?token=" + encodeURIComponent(token()); }}
            style={{ display:"flex", alignItems:"center", gap:8, background: "#fff", color: "#3c4043", border: "1px solid " + C.border, borderRadius: 6, padding: "8px 16px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
            <MicrosoftLogo size={16}/> Connect with Microsoft
          </button>
          <button onClick={() => setShowSmtp(!showSmtp)} style={{ display:"flex", alignItems:"center", gap:8, background: C.accent, color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
            <MailLogo size={16} color="#fff"/> Connect Email (SMTP)
          </button>
        </div>
      </div>
      {showSmtp && (
        <div style={{ marginBottom: 16 }}>
          <SmtpConnectForm
            addToast={addToast}
            onCancel={() => setShowSmtp(false)}
            onConnected={() => { setShowSmtp(false); loadAccounts(); }}
          />
        </div>
      )}
      {loading && <p style={{ fontSize: 12, color: C.textSub }}>Loading accounts...</p>}
      {!loading && accounts.length === 0 && !showSmtp && (
        <div style={{ textAlign: "center", padding: "28px 0", color: "#94a3b8" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: 10, marginBottom: 12 }}>
            <GoogleLogo size={26}/><MicrosoftLogo size={26}/><YahooLogo size={26}/><MailLogo size={26} color="#c7cbe0"/>
          </div>
          <p style={{ fontSize: 13, fontWeight: 500 }}>No email accounts connected yet</p>
          <p style={{ fontSize: 12, marginTop: 4 }}>Connect with Google or Microsoft above for one-click setup, or use Connect Email for any other provider</p>
        </div>
      )}
      {accounts.map(a => (
        <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap", rowGap: 8 }}>
          <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#fff", border: "1px solid " + C.border, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {accountLogo(a, 20)}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>{a.name || a.email}</p>
            <p style={{ fontSize: 11, color: C.textSub, margin: "2px 0 0" }}>{a.email} via {a.host} — Connected {new Date(a.connectedAt).toLocaleDateString()}</p>
            {a.imapConfigured ? (
              <p style={{ fontSize: 11, color: a.lastSyncError ? C.red : C.green, margin: "2px 0 0" }}>
                {a.lastSyncError ? ("Sync error: " + a.lastSyncError) : (a.lastSyncAt ? ("Last synced " + new Date(a.lastSyncAt).toLocaleTimeString()) : "Sync pending — first run within 60s")}
              </p>
            ) : (
              <p style={{ fontSize: 11, color: "#B54708", margin: "2px 0 0" }}>Send-only — no IMAP configured, inbox won't sync</p>
            )}
          </div>
          {a.imapConfigured && (
            <button onClick={() => syncNow(a.email)} disabled={syncingId === a.email} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: syncingId === a.email ? "not-allowed" : "pointer", color: C.accent }}>
              {syncingId === a.email ? "Syncing..." : "Sync Now"}
            </button>
          )}
          {a.isDefault ? (
            <span title="This is the mailbox you logged in with — used to send when no other sender is chosen" style={{ fontSize: 11, background: "#EEF2FF", color: "#4338CA", borderRadius: 99, padding: "2px 10px", fontWeight: 600 }}>Primary</span>
          ) : (
            <button onClick={() => setDefault(a.id)} title="Make this the default sending account" style={{ fontSize: 11, background: "none", border: "1px solid " + C.border, borderRadius: 99, padding: "2px 10px", fontWeight: 600, color: C.textSub, cursor: "pointer" }}>Make primary</button>
          )}
          <span style={{ fontSize: 11, background: C.greenMuted, color: C.green, borderRadius: 99, padding: "2px 10px", fontWeight: 600 }}>Active</span>
          <button onClick={() => disconnect(a.id)} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", color: C.red }}>Disconnect</button>
        </div>
      ))}
    </div>
  );
}
